<template>
  <div class="pa-root">

    <!-- Loading -->
    <div v-if="loading" class="pa-splash">
      <div class="pa-ring"></div>
      <p>Loading your visit details…</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="pa-screen">
      <div class="pa-box pa-box--error">
        <div class="pa-icon pa-icon--error">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        </div>
        <h2>Link Not Found</h2>
        <p>{{ error }}</p>
      </div>
    </div>

    <!-- Complete -->
    <div v-else-if="info && info.all_required_signed" class="pa-screen">
      <div class="pa-box pa-box--success">
        <div class="pa-icon pa-icon--success">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <h2>You're All Set!</h2>
        <p class="pa-sub">All required documents have been signed. See you on <strong>{{ fmtDate(info.visit.scheduled_at) }}</strong>.</p>
        <div class="pa-visit-card">
          <div class="pa-visit-row"><span class="pa-vr-label">Host</span><span class="pa-vr-val">{{ info.visit.host_name }}</span></div>
          <div class="pa-visit-row"><span class="pa-vr-label">Date &amp; Time</span><span class="pa-vr-val">{{ fmtDate(info.visit.scheduled_at) }}</span></div>
          <div class="pa-visit-row"><span class="pa-vr-label">Purpose</span><span class="pa-vr-val">{{ info.visit.purpose || '—' }}</span></div>
        </div>
        <p class="pa-tip">Please bring your invitation email with the QR code on the day for a fast check-in.</p>
        <!-- Add to Calendar on success screen -->
        <div class="pa-cal-section">
          <div class="pa-cal-label">Add to Calendar</div>
          <div class="pa-cal-buttons">
            <a :href="calendarIcsUrl" class="pa-cal-btn">&#8595; Download .ics</a>
            <a :href="googleCalUrl" target="_blank" rel="noopener" class="pa-cal-btn">Google Calendar</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Documents to sign -->
    <div v-else-if="info" class="pa-content">

      <!-- Header -->
      <div class="pa-header">
        <div class="pa-company">{{ info.company }}</div>
        <div class="pa-header-title">Pre-Arrival Documents</div>
        <div class="pa-header-sub">Hello {{ info.visitor.first_name }}, please review and sign the documents below before your visit.</div>
      </div>

      <!-- Visit summary chip row -->
      <div class="pa-visit-chips">
        <span class="pa-chip"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg> {{ fmtDate(info.visit.scheduled_at) }}</span>
        <span class="pa-chip"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> {{ info.visit.host_name }}</span>
      </div>

      <!-- Progress bar -->
      <div class="pa-progress-wrap">
        <div class="pa-progress-label">
          {{ signedRequiredCount }} of {{ info.required_doc_ids.length }} required document{{ info.required_doc_ids.length !== 1 ? 's' : '' }} signed
        </div>
        <div class="pa-progress-bar">
          <div class="pa-progress-fill" :style="{ width: progressPct + '%' }"></div>
        </div>
      </div>

      <!-- Document cards -->
      <div v-for="doc in info.documents" :key="doc.id" class="pa-doc-card" :class="{ 'pa-doc-card--signed': doc.already_signed }">

        <!-- Doc header -->
        <div class="pa-doc-header">
          <div class="pa-doc-title-row">
            <span class="pa-doc-type-badge" :class="'type--' + doc.type">{{ typeLabel(doc.type) }}</span>
            <span v-if="isRequired(doc.id)" class="pa-required-badge">Required</span>
            <span v-if="doc.already_signed" class="pa-signed-badge">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>
              Signed {{ fmtShortDate(doc.signed_at) }}
            </span>
          </div>
          <div class="pa-doc-name">{{ doc.name }}</div>
          <div v-if="doc.current_version" class="pa-doc-version">Version {{ doc.current_version.version_number }} · Effective {{ fmtShortDate(doc.current_version.effective_from) }}</div>
        </div>

        <!-- Doc content (scrollable) -->
        <div v-if="doc.current_version && !doc.already_signed" class="pa-doc-content" :class="{ 'pa-doc-content--expanded': expanded[doc.id] }">
          <div class="pa-doc-text" v-html="renderContent(doc.current_version.content)"></div>
          <button v-if="!expanded[doc.id]" class="pa-read-more" @click="expand(doc.id)">Read full document ↓</button>
        </div>

        <!-- Sign action -->
        <div v-if="!doc.already_signed && doc.current_version" class="pa-doc-action">
          <label class="pa-agree-label">
            <input type="checkbox" v-model="agreed[doc.id]" :disabled="!expanded[doc.id]" />
            <span :class="{ 'pa-agree-text--disabled': !expanded[doc.id] }">
              I have read and agree to this document
            </span>
          </label>
          <button
            class="pa-sign-btn"
            :disabled="!agreed[doc.id] || signing[doc.id]"
            @click="signDoc(doc)"
          >
            <svg v-if="signing[doc.id]" class="pa-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
            {{ signing[doc.id] ? 'Signing…' : 'Sign Document' }}
          </button>
        </div>

        <!-- Already signed state -->
        <div v-else-if="doc.already_signed" class="pa-signed-banner">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
          Signed on {{ fmtShortDate(doc.signed_at) }}
        </div>

        <!-- Quiz (induction docs with questions) -->
        <div v-if="doc.already_signed && doc.has_quiz" class="pa-quiz-wrap">
          <!-- Already passed -->
          <div v-if="doc.quiz_result && doc.quiz_result.passed" class="pa-quiz-passed">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
            Induction passed — {{ doc.quiz_result.score }}%
          </div>
          <!-- Previously failed, can retry -->
          <div v-else-if="doc.quiz_result && !doc.quiz_result.passed" class="pa-quiz-failed">
            Score: {{ doc.quiz_result.score }}% (pass mark: {{ doc.quiz_pass_mark }}%)
            <button class="pa-quiz-start-btn" @click="startQuiz(doc)">Retake Quiz</button>
          </div>
          <!-- Not yet attempted -->
          <div v-else class="pa-quiz-prompt">
            <span>This document has a required quiz.</span>
            <button class="pa-quiz-start-btn" @click="startQuiz(doc)">Start Quiz</button>
          </div>

          <!-- Active quiz -->
          <div v-if="activeQuiz && activeQuiz.docId === doc.id" class="pa-quiz-active">
            <div class="pa-quiz-header">
              Induction Quiz — Question {{ activeQuiz.current + 1 }} of {{ activeQuiz.questions.length }}
            </div>
            <div class="pa-quiz-question">{{ activeQuiz.questions[activeQuiz.current].question }}</div>
            <div class="pa-quiz-options">
              <label
                v-for="(opt, oi) in activeQuiz.questions[activeQuiz.current].options"
                :key="oi"
                class="pa-quiz-option"
                :class="{
                  'pa-quiz-option--selected': activeQuiz.answers[activeQuiz.current] === oi,
                  'pa-quiz-option--correct': activeQuiz.submitted && oi === activeQuiz.questions[activeQuiz.current].correct_index,
                  'pa-quiz-option--wrong':   activeQuiz.submitted && activeQuiz.answers[activeQuiz.current] === oi && oi !== activeQuiz.questions[activeQuiz.current].correct_index
                }"
              >
                <input
                  type="radio"
                  :name="'q' + activeQuiz.current"
                  :value="oi"
                  v-model="activeQuiz.answers[activeQuiz.current]"
                  :disabled="activeQuiz.submitted"
                />
                {{ opt }}
              </label>
            </div>
            <div class="pa-quiz-nav">
              <button
                v-if="!activeQuiz.submitted"
                class="pa-sign-btn"
                :disabled="activeQuiz.answers[activeQuiz.current] === undefined || activeQuiz.submitting"
                @click="quizNext(doc)"
              >
                {{ activeQuiz.current !== activeQuiz.questions.length - 1 ? 'Next →' : (activeQuiz.submitting ? 'Submitting…' : 'Submit Quiz') }}
              </button>
              <button v-else class="pa-quiz-start-btn" @click="activeQuiz = null; loadInfo()">Done</button>
            </div>
          </div>
        </div>

      </div>

      <!-- No documents message -->
      <div v-if="!info.documents.length" class="pa-no-docs">
        <p>No documents are required for your visit. You're all set!</p>
      </div>

      <div class="pa-footer">Powered by <strong>Mapiibs</strong> · Visitor Management</div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'VisitorPreArrival',
  data() {
    return {
      loading: true,
      error: null,
      companyCode: '',
      businessId: '',
      token: '',
      info: null,
      agreed: {},
      expanded: {},
      signing: {},
      activeQuiz: null,
    }
  },
  computed: {
    signedRequiredCount() {
      if (!this.info) return 0
      return this.info.documents.filter(d =>
        this.info.required_doc_ids.includes(d.id) && d.already_signed
      ).length
    },
    progressPct() {
      const total = this.info?.required_doc_ids?.length ?? 0
      if (!total) return 100
      return Math.round((this.signedRequiredCount / total) * 100)
    },
    calendarIcsUrl() {
      if (!this.companyCode || !this.businessId || !this.token) return '#'
      const base = window.location.origin
      return `${base}/api/public/visitors/${this.companyCode}/${this.businessId}/pre-arrival/${this.token}/calendar.ics`
    },
    googleCalUrl() {
      if (!this.info?.visit?.scheduled_at) return '#'
      const start = new Date(this.info.visit.scheduled_at)
      const end   = new Date(start.getTime() + 60 * 60 * 1000)
      const fmt   = d => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
      const params = new URLSearchParams({
        action:   'TEMPLATE',
        text:     `Visit to ${this.info.company}`,
        dates:    `${fmt(start)}/${fmt(end)}`,
        details:  this.info.visit.host_name ? `Visiting ${this.info.visit.host_name}` : '',
        location: this.info.visit.location ?? '',
      })
      return `https://calendar.google.com/calendar/render?${params.toString()}`
    },
  },
  created() {
    this.companyCode = this.$route.params.companyCode
    this.businessId  = this.$route.params.businessId
    this.token       = this.$route.params.token
    this.loadInfo()
  },
  methods: {
    async loadInfo() {
      this.loading = true
      try {
        const r = await axios.get(
          `public/visitors/${this.companyCode}/${this.businessId}/pre-arrival/${this.token}`
        )
        this.info = r.data.data
        // Initialise agreed/expanded state per doc
        this.info.documents.forEach(doc => {
          if (!doc.already_signed) {
            this.$set ? this.$set(this.agreed, doc.id, false) : (this.agreed[doc.id] = false)
            this.$set ? this.$set(this.expanded, doc.id, false) : (this.expanded[doc.id] = false)
          }
        })
      } catch (e) {
        this.error = e.response?.data?.message ?? 'This pre-arrival link is invalid or has expired.'
      } finally {
        this.loading = false
      }
    },
    expand(docId) {
      this.expanded[docId] = true
    },
    async signDoc(doc) {
      this.signing[doc.id] = true
      try {
        await axios.post(
          `public/visitors/${this.companyCode}/${this.businessId}/pre-arrival/${this.token}/sign`,
          { document_version_id: doc.current_version.id }
        )
        // Refresh to get updated signed state
        await this.loadInfo()
      } catch (e) {
        alert(e.response?.data?.message ?? 'Failed to sign document. Please try again.')
      } finally {
        this.signing[doc.id] = false
      }
    },
    isRequired(docId) {
      return this.info?.required_doc_ids?.includes(docId)
    },
    typeLabel(type) {
      return { nda: 'NDA', induction: 'Induction', policy: 'Policy', other: 'Document' }[type] ?? type
    },
    renderContent(text) {
      if (!text) return ''
      // Preserve line breaks
      return text.replace(/\n/g, '<br>')
    },
    startQuiz(doc) {
      const questions = doc.current_version?.quiz_questions ?? []
      if (!questions.length) return
      this.activeQuiz = {
        docId:       doc.id,
        versionId:   doc.current_version.id,
        questions,
        answers:     new Array(questions.length).fill(undefined),
        current:     0,
        submitting:  false,
        submitted:   false,
      }
    },
    async quizNext() {
      if (this.activeQuiz.current < this.activeQuiz.questions.length - 1) {
        this.activeQuiz.current++
        return
      }
      // Final question — submit
      this.activeQuiz.submitting = true
      const answers = {}
      this.activeQuiz.answers.forEach((a, i) => { answers[i] = a })
      try {
        await axios.post(
          `public/visitors/${this.companyCode}/${this.businessId}/pre-arrival/${this.token}/quiz`,
          { document_version_id: this.activeQuiz.versionId, answers }
        )
        this.activeQuiz.submitted = true
      } catch (e) {
        alert(e.response?.data?.message ?? 'Failed to submit quiz. Please try again.')
      } finally {
        this.activeQuiz.submitting = false
      }
    },
    fmtDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleString('en-GB', {
        weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    },
    fmtShortDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    },
  },
}
</script>

<style scoped>
* { box-sizing: border-box; }
.pa-root { min-height: 100vh; background: #f0f4f8; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif; }

/* Splash / loading */
.pa-splash { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; gap: 16px; color: #7a90b0; font-size: 14px; }
.pa-ring { width: 36px; height: 36px; border: 3px solid #e4eaf2; border-top-color: #004AAD; border-radius: 50%; animation: spin .8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Centred screens (error / success) */
.pa-screen { display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 24px; }
.pa-box { background: #fff; border-radius: 16px; padding: 40px 36px; max-width: 480px; width: 100%; text-align: center; box-shadow: 0 4px 24px rgba(0,0,0,.08); }
.pa-icon { width: 56px; height: 56px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; }
.pa-icon--error   { background: #fef2f2; color: #dc2626; }
.pa-icon--success { background: #004AAD; color: #fff; }
.pa-box h2 { font-size: 22px; font-weight: 800; color: #1a2b45; margin: 0 0 10px; }
.pa-sub { font-size: 14px; color: #4a5e78; line-height: 1.6; margin: 0 0 20px; }
.pa-visit-card { background: #f8fafc; border: 1px solid #e4eaf2; border-radius: 10px; overflow: hidden; text-align: left; margin-bottom: 18px; }
.pa-visit-row { display: flex; padding: 10px 16px; border-bottom: 1px solid #edf0f5; }
.pa-visit-row:last-child { border-bottom: none; }
.pa-vr-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #aab8cc; width: 100px; flex-shrink: 0; }
.pa-vr-val { font-size: 13px; font-weight: 600; color: #1a2b45; }
.pa-tip { font-size: 12px; color: #7a90b0; line-height: 1.6; margin: 0; }

/* Main content layout */
.pa-content { max-width: 680px; margin: 0 auto; padding: 32px 16px 48px; }

/* Header */
.pa-header { text-align: center; margin-bottom: 24px; }
.pa-company { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; color: #004AAD; margin-bottom: 8px; }
.pa-header-title { font-size: 24px; font-weight: 800; color: #1a2b45; margin-bottom: 6px; }
.pa-header-sub { font-size: 14px; color: #4a5e78; line-height: 1.5; }

/* Visit chips */
.pa-visit-chips { display: flex; flex-wrap: wrap; gap: 8px; justify-content: center; margin-bottom: 20px; }
.pa-chip { display: inline-flex; align-items: center; gap: 5px; background: #fff; border: 1px solid #e4eaf2; border-radius: 20px; padding: 5px 12px; font-size: 12px; color: #4a5e78; font-weight: 600; }

/* Progress */
.pa-progress-wrap { background: #fff; border: 1px solid #e4eaf2; border-radius: 10px; padding: 14px 18px; margin-bottom: 20px; }
.pa-progress-label { font-size: 12px; font-weight: 600; color: #4a5e78; margin-bottom: 8px; }
.pa-progress-bar { background: #f0f4f8; border-radius: 6px; height: 8px; overflow: hidden; }
.pa-progress-fill { height: 100%; background: #004AAD; border-radius: 6px; transition: width .4s ease; }

/* Document card */
.pa-doc-card { background: #fff; border: 1px solid #e4eaf2; border-radius: 12px; margin-bottom: 16px; overflow: hidden; transition: border-color .2s; }
.pa-doc-card--signed { border-color: #bbf7d0; }

.pa-doc-header { padding: 18px 20px 14px; border-bottom: 1px solid #f0f4f8; }
.pa-doc-title-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 8px; }
.pa-doc-name { font-size: 16px; font-weight: 700; color: #1a2b45; margin-bottom: 4px; }
.pa-doc-version { font-size: 11px; color: #aab8cc; }

/* Type badge */
.pa-doc-type-badge { display: inline-block; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; padding: 2px 8px; border-radius: 20px; }
.type--nda       { background: #eff6ff; color: #2563eb; }
.type--induction { background: #f5f3ff; color: #7c3aed; }
.type--policy    { background: #f0fdf4; color: #15803d; }
.type--other     { background: #f1f5f9; color: #64748b; }

.pa-required-badge { font-size: 10px; font-weight: 700; text-transform: uppercase; background: #fff7ed; color: #c2410c; padding: 2px 8px; border-radius: 20px; }
.pa-signed-badge   { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; color: #16a34a; background: #f0fdf4; padding: 2px 8px; border-radius: 20px; }

/* Doc content area */
.pa-doc-content { padding: 0 20px; max-height: 200px; overflow: hidden; position: relative; }
.pa-doc-content--expanded { max-height: none; overflow: visible; }
.pa-doc-content:not(.pa-doc-content--expanded)::after {
  content: '';
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 60px;
  background: linear-gradient(transparent, #fff);
  pointer-events: none;
}
.pa-doc-text { font-size: 13px; color: #4a5e78; line-height: 1.7; padding: 16px 0; white-space: pre-wrap; }
.pa-read-more { display: block; width: 100%; text-align: center; background: none; border: none; color: #004AAD; font-size: 12px; font-weight: 600; cursor: pointer; padding: 8px 0 14px; font-family: inherit; }
.pa-read-more:hover { text-decoration: underline; }

/* Sign action */
.pa-doc-action { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; padding: 14px 20px; border-top: 1px solid #f0f4f8; background: #fafbfd; }
.pa-agree-label { display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 13px; color: #1a2b45; font-weight: 500; }
.pa-agree-label input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; accent-color: #004AAD; }
.pa-agree-text--disabled { color: #aab8cc; }
.pa-sign-btn { display: inline-flex; align-items: center; gap: 6px; background: #004AAD; color: #fff; font-size: 13px; font-weight: 700; padding: 0 20px; height: 38px; border-radius: 8px; border: none; cursor: pointer; font-family: inherit; white-space: nowrap; }
.pa-sign-btn:hover:not(:disabled) { background: #003b8e; }
.pa-sign-btn:disabled { opacity: .45; cursor: not-allowed; }

/* Signed banner */
.pa-signed-banner { display: flex; align-items: center; gap: 8px; padding: 12px 20px; background: #f0fdf4; color: #16a34a; font-size: 13px; font-weight: 600; }

/* Spinner */
.pa-spinner { animation: spin .8s linear infinite; }

/* No docs */
.pa-no-docs { text-align: center; padding: 40px 20px; color: #7a90b0; font-size: 14px; }

/* Footer */
.pa-footer { text-align: center; font-size: 12px; color: #aab8cc; margin-top: 32px; }

/* Calendar section (on success screen) */
.pa-cal-section { margin-top: 20px; padding-top: 18px; border-top: 1px solid #e4eaf2; }
.pa-cal-label { font-size: 12px; font-weight: 700; color: #4a5e78; margin-bottom: 10px; text-transform: uppercase; letter-spacing: .05em; }
.pa-cal-buttons { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; }
.pa-cal-btn { display: inline-block; background: #f0f4f8; color: #1a2b45; text-decoration: none; font-size: 12px; font-weight: 600; padding: 8px 18px; border-radius: 8px; border: 1px solid #dde5f0; white-space: nowrap; }
.pa-cal-btn:hover { background: #e4eaf2; }

/* Quiz section */
.pa-quiz-wrap { border-top: 1px solid #f0f4f8; padding: 14px 20px; background: #fafbfd; }
.pa-quiz-passed { display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; color: #15803d; background: #f0fdf4; border-radius: 8px; padding: 10px 14px; }
.pa-quiz-failed { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; font-size: 13px; color: #c2410c; background: #fff7ed; border-radius: 8px; padding: 10px 14px; font-weight: 600; }
.pa-quiz-prompt { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; font-size: 13px; color: #4a5e78; font-weight: 500; }
.pa-quiz-start-btn { display: inline-flex; align-items: center; background: #7c3aed; color: #fff; font-size: 12px; font-weight: 700; padding: 0 16px; height: 34px; border-radius: 8px; border: none; cursor: pointer; font-family: inherit; white-space: nowrap; }
.pa-quiz-start-btn:hover { background: #6d28d9; }

.pa-quiz-active { margin-top: 14px; background: #fff; border: 1px solid #e4eaf2; border-radius: 10px; padding: 18px; }
.pa-quiz-header { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: #7c3aed; margin-bottom: 10px; }
.pa-quiz-question { font-size: 14px; font-weight: 700; color: #1a2b45; line-height: 1.5; margin-bottom: 14px; }
.pa-quiz-options { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
.pa-quiz-option { display: flex; align-items: center; gap: 10px; padding: 10px 14px; border: 1px solid #e4eaf2; border-radius: 8px; cursor: pointer; font-size: 13px; color: #1a2b45; font-weight: 500; transition: border-color .15s, background .15s; }
.pa-quiz-option:hover { background: #f8fafc; }
.pa-quiz-option--selected { border-color: #7c3aed; background: #f5f3ff; }
.pa-quiz-option--correct  { border-color: #16a34a; background: #f0fdf4; color: #15803d; }
.pa-quiz-option--wrong    { border-color: #dc2626; background: #fef2f2; color: #dc2626; }
.pa-quiz-option input[type="radio"] { accent-color: #7c3aed; flex-shrink: 0; }
.pa-quiz-nav { display: flex; justify-content: flex-end; gap: 10px; }

@media (max-width: 480px) {
  .pa-doc-action { flex-direction: column; align-items: flex-start; }
  .pa-sign-btn { width: 100%; justify-content: center; }
}
</style>
